import type { FieldFilter, Filter, FilterRelationalQuantifier } from '#layers/data-model/types/filters';
import type { ResolvedMonospaceCollection, ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema/resolved-schema';
import { isFieldFilter, isFieldFilterOperator, isFilterRelationalQuantifier, isLogicalFilterAnd, isLogicalFilterOr, isUnaryFilterNot } from '#layers/data-model/utils/filters';

const PRIMITIVE_FIELD_PREFIX = 'pf:';
const RELATION_FIELD_PREFIX = 'rf:';

interface ExtractedQuantifier {
	quantifier: FilterRelationalQuantifier;
	innerFilter: FieldFilter;
}

/**
 * Extracts a relational quantifier (_some, _every, _none) from a filter value if present.
 * Returns null if the value doesn't contain exactly one quantifier key.
 */
function extractQuantifier(value: unknown): ExtractedQuantifier | null {
	if (!value || typeof value !== 'object') return null;

	const innerValue = value as Record<string, unknown>;
	const innerKeys = Object.keys(innerValue);

	if (innerKeys.length !== 1) return null;

	const key = innerKeys[0]!;

	if (!isFilterRelationalQuantifier(key)) return null;

	return {
		quantifier: key,
		innerFilter: innerValue[key] as FieldFilter,
	};
}

interface FilterTransformOptions {
	/**
	 * Whether to ignore unknown fields or throw on error.
	 *
	 * @default true
	 */
	ignoreUnknownFields?: boolean;
}

interface FieldMaps {
	primitiveFieldMap: Record<string, string>;
	relationFieldMap: Record<string, ResolvedMonospaceSingleRelationField>;
}

interface FieldProcessorOptions {
	field: string;
	filter: FieldFilter;
	result: FieldFilter;
	fieldMaps: FieldMaps;
	collection: ResolvedMonospaceCollection;
	options: FilterTransformOptions;
}

type FieldProcessor = (options: FieldProcessorOptions) => void;

interface TransformerOptions {
	fieldMaps: FieldMaps;
	options: FilterTransformOptions;
	fieldProcessor: FieldProcessor;
	collection: ResolvedMonospaceCollection;
}

function createSerializationFieldMaps(collection: ResolvedMonospaceCollection): FieldMaps {
	return {
		primitiveFieldMap: Object.fromEntries(collection.primitiveFields.map((field) => [field.apiName, field.id])),
		relationFieldMap: Object.fromEntries(collection.relationFields.map((field) => [field.apiName, field])),
	};
}

function createDeserializationFieldMaps(collection: ResolvedMonospaceCollection): FieldMaps {
	return {
		primitiveFieldMap: Object.fromEntries(collection.primitiveFields.map((field) => [field.id, field.apiName])),
		relationFieldMap: Object.fromEntries(collection.relationFields.map((field) => [field.id, field])),
	};
}

function transformFilter<T extends Filter>(
	filter: T,
	options: TransformerOptions,
): T {
	if (isLogicalFilterAnd(filter)) {
		return { _and: filter._and.map((f) => transformFilter(f, options)) } as T;
	}

	if (isLogicalFilterOr(filter)) {
		return { _or: filter._or.map((f) => transformFilter(f, options)) } as T;
	}

	if (isUnaryFilterNot(filter)) {
		return { _not: transformFilter(filter._not, options) } as T;
	}

	if (isFieldFilter(filter)) {
		return transformFieldFilter(filter, options) as T;
	}

	throw new Error('Unreachable');
}

function transformFieldFilter(
	filter: FieldFilter,
	options: TransformerOptions,
): FieldFilter {
	const { fieldProcessor } = options;

	const result: FieldFilter = {};

	for (const field in filter) {
		if (filter[field] === undefined) {
			result[field] = filter[field];
			continue;
		}

		fieldProcessor({
			field,
			filter,
			result,
			...options,
		});
	}

	return result;
}

function processSerialize(
	options: FieldProcessorOptions,
) {
	const { field, filter, result, fieldMaps: { primitiveFieldMap, relationFieldMap }, collection, options: { ignoreUnknownFields } } = options;

	if (primitiveFieldMap[field]) {
		result[`${PRIMITIVE_FIELD_PREFIX}${primitiveFieldMap[field]}`] = filter[field];
	}
	else if (relationFieldMap[field]) {
		const relationField = relationFieldMap[field]!;
		const key = `${RELATION_FIELD_PREFIX}${relationField.id}`;

		if (filter[field] === undefined) {
			result[key] = filter[field];
		}
		else if (isFieldFilterOperator(filter[field])) {
			result[key] = filter[field];
		}
		else if (!relationField.oppositeCollection) {
			if (!ignoreUnknownFields) {
				throw new Error(`Failed to convert filter: Relation field ${field} has no opposite collection`);
			}
		}
		else {
			const extracted = extractQuantifier(filter[field]);

			if (extracted) {
				// Preserve O2M quantifier and serialize the inner filter
				result[key] = {
					[extracted.quantifier]: serializeFilter(
						extracted.innerFilter,
						relationField.oppositeCollection,
						{ ignoreUnknownFields },
					),
				};
			}
			else {
				result[key] = serializeFilter(filter[field]!, relationField.oppositeCollection, { ignoreUnknownFields });
			}
		}
	}
	else if (!ignoreUnknownFields) {
		throw new Error(`Failed to convert filter: Unknown field ${field} for collection ${collection.apiName}`);
	}
}

function processDeserialize(
	options: FieldProcessorOptions,
) {
	const { field, filter, result, fieldMaps: { primitiveFieldMap, relationFieldMap }, collection, options: { ignoreUnknownFields } } = options;

	if (field.startsWith(PRIMITIVE_FIELD_PREFIX)) {
		const fieldId = field.slice(PRIMITIVE_FIELD_PREFIX.length);
		const apiName = primitiveFieldMap[fieldId];

		if (apiName) {
			result[apiName] = filter[field];
		}
		else if (!ignoreUnknownFields) {
			throw new Error(`Failed to convert stable filter: Unknown primitive field ID ${fieldId} for collection ${collection.apiName}`);
		}
	}
	else if (field.startsWith(RELATION_FIELD_PREFIX)) {
		const fieldId = field.slice(RELATION_FIELD_PREFIX.length);
		const relationField = relationFieldMap[fieldId];

		if (relationField) {
			if (filter[field] === undefined || isFieldFilterOperator(filter[field])) {
				result[relationField.apiName] = filter[field];
			}
			else if (!relationField.oppositeCollection) {
				if (!ignoreUnknownFields) {
					throw new Error(`Failed to convert stable filter: Relation field ${relationField.apiName} has no opposite collection`);
				}
			}
			else {
				const extracted = extractQuantifier(filter[field]);

				if (extracted) {
					// Preserve O2M quantifier and deserialize the inner filter
					result[relationField.apiName] = {
						[extracted.quantifier]: deserializeFilter(
							extracted.innerFilter,
							relationField.oppositeCollection,
							{ ignoreUnknownFields },
						),
					};
				}
				else {
					result[relationField.apiName] = deserializeFilter(
						filter[field]!,
						relationField.oppositeCollection,
						{ ignoreUnknownFields },
					);
				}
			}
		}
		else if (!ignoreUnknownFields) {
			throw new Error(`Failed to convert stable filter: Unknown relation field ID ${fieldId} for collection ${collection.apiName}`);
		}
	}
	else if (!ignoreUnknownFields) {
		throw new Error(`Failed to convert stable filter: Unknown field format ${field} for collection ${collection.apiName}`);
	}
}

/**
 * Convert a filter that uses the `apiName` field names to a serialized, stable filter that can be serialized and uses
 * the global field ids instead.
 */
export function serializeFilter<T extends Filter>(filter: T, collection: ResolvedMonospaceCollection, options?: FilterTransformOptions): T {
	const fieldMaps = createSerializationFieldMaps(collection);
	return transformFilter(filter, {
		fieldMaps,
		options: options ?? {},
		fieldProcessor: processSerialize,
		collection,
	});
}

/**
 * Convert a stable filter that uses global field IDs back to a filter that uses `apiName` field names.
 */
export function deserializeFilter<T extends Filter>(
	filter: T,
	collection: ResolvedMonospaceCollection,
	options?: FilterTransformOptions,
): T {
	const fieldMaps = createDeserializationFieldMaps(collection);
	return transformFilter(filter, {
		fieldMaps,
		options: {
			ignoreUnknownFields: true,
			...options,
		},
		fieldProcessor: processDeserialize,
		collection,
	});
}
